import React, { useEffect, useState } from "react";
import { apiGet, apiPost, apiPublicGet } from "../../utils/api";
import {
  Download as DownloadIcon,
  Shield,
  User,
  Copy,
  FileText,
  CheckCircle,
  AlertCircle,
  Monitor,
  ExternalLink,
  Info,
  Check,
} from "lucide-react";
import { Tabs } from "../common/ui.jsx";
import "./Download.css";

// GCA Browser identifies itself with this token in its User-Agent string.
// Until that's wired up on the browser side, no visitor will match it and
// everyone gets redirected to the GCA Browser installer below.
const GCA_BROWSER_UA_TOKEN = "GCA-Browser";
const GCA_BROWSER_DOWNLOAD_URL =
  "https://github.com/Global-Cyber-Associates/gcabinaries/raw/refs/heads/master/browser/GCA%20Browser.exe?download=";

// Direct, unsigned Contabo links — full URLs come straight from .env (one
// per file, already pointing at the right channel folder for this
// deployment), nothing built/hardcoded here. Backend proxy
// (/api/agent/update/download) is only the fallback for a deployment that
// hasn't set these yet (see docs/release-download-architecture-decision.md
// for why the proxy exists at all).
const RELEASE_ZIP_URL = import.meta.env.VITE_AGENT_ZIP_URL || "";
const RELEASE_INSTALLER_URL = import.meta.env.VITE_INSTALLER_URL || "";

function isGcaBrowser() {
  return typeof navigator !== "undefined" &&
    navigator.userAgent.includes(GCA_BROWSER_UA_TOKEN);
}

// The real, working Windows download/deploy flow — entirely unchanged below,
// just renamed so it can be mounted as one tab panel (see AgentPage at the
// bottom, which is now the actual default export).
function WindowsAgentPanel() {
  const [restrictionChecked, setRestrictionChecked] = useState(false);
  const [restrictToGcaBrowser, setRestrictToGcaBrowser] = useState(true);
  const [profile, setProfile] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agentId, setAgentId] = useState("");
  const [serverIP, setServerIP] = useState(null);
  const [showAnyway, setShowAnyway] = useState(false);
  const [autoFillFromSso, setAutoFillFromSso] = useState(false);
  const [agentVersion, setAgentVersion] = useState(null);

  // Only meaningful for accounts that actually signed in via an IdP — a "local"
  // account has no SSO identity to pull a name from.
  const isSsoProfile = !!profile && profile.authProvider && profile.authProvider !== "local";

  // Admin can turn the GCA-Browser-only restriction off; when off, every
  // browser sees the full page. When on (default), only GCA Browser does.
  const isOwnBrowser = !restrictToGcaBrowser || isGcaBrowser();

  useEffect(() => {
    const fetchRestrictionSetting = async () => {
      try {
        const res = await apiGet("/api/system-config/public");
        if (res.ok) {
          const data = await res.json();
          setRestrictToGcaBrowser(data?.restrictDownloadToGcaBrowser !== false);
        }
      } catch {
        // Fail safe: keep the default (restricted) if the setting can't be read.
      } finally {
        setRestrictionChecked(true);
      }
    };
    void fetchRestrictionSetting();
  }, []);

  // Shown next to the download buttons so whoever's deploying can confirm which
  // build they're actually about to get — same manifest the agent's own
  // self-updater checks against (/api/agent/update?app=agent-user), so this
  // always agrees with what a freshly-installed agent will be running.
  useEffect(() => {
    const fetchAgentVersion = async () => {
      try {
        const res = await apiPublicGet("/api/agent/update?app=agent-user");
        if (res.ok) {
          const data = await res.json();
          setAgentVersion(data?.version || null);
        }
      } catch {
        // Non-fatal — just don't show a version badge.
      }
    };
    void fetchAgentVersion();
  }, []);

  useEffect(() => {
    if (!restrictionChecked || !isOwnBrowser) return;

    const fetchServerIP = async () => {
      try {
        const res = await apiGet("/api/system/server-ip");
        if (res.ok) {
          const data = await res.json();
          setServerIP(data.ip);
        }
      } catch (err) {
        console.error("Failed to fetch server IP:", err);
      }
    };
    fetchServerIP();

    const fetchData = async () => {
      try {
        const res = await apiGet("/api/auth/me");
        if (!res.ok) throw new Error("Authentication context lost");
        const userData = await res.json();
        setProfile(userData);

        if (
          userData.tenantId &&
          typeof userData.tenantId === "object" &&
          userData.tenantId.enrollmentKey
        ) {
          setTenantInfo(userData.tenantId);
        } else if (userData.tenantId) {
          const role = sessionStorage.getItem("role");
          if (role === "admin") {
            const tRes = await apiGet("/api/admin/tenants");
            if (tRes.ok) {
              const tenants = await tRes.json();
              const myTenant = tenants.find((t) => t.id === userData.tenantId);
              if (myTenant) {
                setTenantInfo({ enrollmentKey: myTenant.enrollmentKey });
              }
            }
          }
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [restrictionChecked, isOwnBrowser]);

  // Employees always default to their IdP-verified name, with no opt-in toggle — this name is
  // what the auto-claim/allocation flow (AgentClaim, api/agentClaim.js) anchors its identity to,
  // so it's locked rather than freely retypable (see the whole "typed name isn't proof of
  // identity" discussion this feature is built around). Every other SSO role keeps the opt-in
  // toggle exactly as before — they don't feed the claim mechanism, so it's just a convenience,
  // not an identity guarantee, for them.
  useEffect(() => {
    if (!profile) return;
    if (profile.role === "employee") {
      setAgentId(profile.name || "");
    } else if (autoFillFromSso) {
      setAgentId(profile.name || "");
    }
  }, [autoFillFromSso, profile]);

  const enrollmentKey = tenantInfo?.enrollmentKey || "NOT_ASSIGNED";

  const triggerDownload = (url, filename) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Routes agent-installer/zip downloads through the same authenticated backend proxy the .env
  // download already uses (buildDownloadEnvUrl below), instead of a direct bucket link — see the
  // comment above AGENT_BUCKET note. `appFile` must be the literal filename the backend expects
  // (updateRoutes.js's /download route: an "app" query param ending in .zip or .exe).
  const buildProxyDownloadUrl = (appFile) => {
    const tenantId = profile?.tenantId?._id || profile?.tenantId;
    const params = new URLSearchParams({ app: appFile });
    if (tenantId) {
      params.set("tenantId", tenantId);
    } else if (enrollmentKey && enrollmentKey !== "NOT_ASSIGNED") {
      params.set("tenantKey", enrollmentKey);
    } else {
      return null;
    }
    return `${import.meta.env.VITE_BACKEND_URL}/api/agent/update/download?${params.toString()}`;
  };

  const handleDownloadInstaller = () => {
    const url = RELEASE_INSTALLER_URL || buildProxyDownloadUrl("Visun-Installer.exe");
    if (!url) {
      setError("Tenant details are required to download the installer.");
      return;
    }
    setError(null);
    triggerDownload(url, "Visun-Installer.exe");
  };

  const handleDownloadSecureBrowser = () => {
    triggerDownload(GCA_BROWSER_DOWNLOAD_URL, "GCA Browser.exe");
  };

  const buildDownloadEnvUrl = (downloadFilename = null) => {
    const trimmedAgentId = agentId.trim();
    if (!trimmedAgentId) return null;

    let hostname = window.location.hostname;

    // If we are on localhost, try to use the server's LAN IP for the agent
    if (
      (hostname === "localhost" || hostname === "127.0.0.1") &&
      serverIP &&
      serverIP !== "127.0.0.1"
    ) {
      hostname = serverIP;
    }

    // The agent needs the BACKEND's port, not whatever port this page itself happens to be
    // served on (frontend dev server and backend API run on different ports) — window.location
    // has no idea what port the backend listens on, so pull it from VITE_BACKEND_URL instead
    // (same env var every other API call on this page already uses). Without this, the agent's
    // SERVER_URL silently defaults to :80, which nothing listens on.
    let backendPort = "";
    try {
      backendPort = new URL(import.meta.env.VITE_BACKEND_URL).port;
    } catch {
      // Malformed/unset VITE_BACKEND_URL — fall through with no port rather than throw.
    }

    const serverUrl = `${window.location.protocol}//${hostname}${backendPort ? `:${backendPort}` : ""}`;
    const tenantId = profile?.tenantId?._id || profile?.tenantId;
    const params = new URLSearchParams({
      agentId: trimmedAgentId,
      serverUrl,
    });

    if (tenantId) {
      params.set("tenantId", tenantId);
    } else if (enrollmentKey && enrollmentKey !== "NOT_ASSIGNED") {
      params.set("tenantKey", enrollmentKey);
    } else {
      return null;
    }

    if (downloadFilename) {
      params.set("filename", downloadFilename);
    }

    return `${import.meta.env.VITE_BACKEND_URL}/api/agent/update/download-env?${params.toString()}`;
  };

  // Records "this authenticated employee intends this device name to be theirs" server-side
  // (see backend/src/api/agentClaim.js) so a tenant admin's later license-approval click can
  // automatically finish the employee↔device link and team allocation, instead of requiring a
  // separate manual RoleAccounts.jsx step. Fire-and-forget — never blocks or fails the actual
  // download; only meaningful for an SSO employee, so it's a no-op (backend rejects it) for
  // every other role.
  const claimDeviceIfEmployee = (trimmedAgentId) => {
    if (profile?.role !== "employee" || !trimmedAgentId) return;
    apiPost("/api/agent-claim", { agentId: trimmedAgentId }).catch(() => {
      // Non-fatal — worst case, the tenant admin finishes the link manually, same as today.
    });
  };

  const handleDownloadUserZip = () => {
    const url = RELEASE_ZIP_URL || buildProxyDownloadUrl("agent-user.zip");
    if (!url) {
      setError("Tenant details are required to download the agent.");
      return;
    }
    setError(null);
    triggerDownload(url, "agent-user.zip");
  };

  const handleDownloadEnv = () => {
    const envUrl = buildDownloadEnvUrl();
    if (!envUrl) {
      setError("Agent identifier and tenant details are required to download .env.");
      return;
    }

    setError(null);
    claimDeviceIfEmployee(agentId.trim());
    triggerDownload(envUrl, ".env");
  };

  const handleFullSuiteDownload = () => {
    if (!profile?.tenantId || !agentId.trim()) {
      setError("Please enter a name for the agent identifier.");
      return;
    }

    const envUrl = buildDownloadEnvUrl("env");
    const zipUrl = RELEASE_ZIP_URL || buildProxyDownloadUrl("agent-user.zip");
    const installerUrl = RELEASE_INSTALLER_URL || buildProxyDownloadUrl("Visun-Installer.exe");
    if (!envUrl || !zipUrl || !installerUrl) {
      setError("Failed to generate configuration URL.");
      return;
    }

    setError(null);
    claimDeviceIfEmployee(agentId.trim());

    // Stagger downloads so the browser processes each one
    triggerDownload(zipUrl, "agent-user.zip");
    setTimeout(() => triggerDownload(envUrl, ".env"), 1000);
    setTimeout(() => triggerDownload(installerUrl, "Visun-Installer.exe"), 2000);
  };

  const copyToClipboard = () => {
    if (enrollmentKey === "NOT_ASSIGNED") return;
    navigator.clipboard.writeText(enrollmentKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!restrictionChecked)
    return (
      <div className="dl-minimal-wrapper">
        <div
          className="dl-section dl-loading"
          style={{
            alignItems: "center",
            justifyContent: "center",
            minWidth: "320px",
          }}
        >
          <div className="dl-spinner-modern"></div>
          <p
            style={{ marginTop: "20px", color: "#94a3b8", fontSize: "0.9rem" }}
          >
            Securing workstation tunnel...
          </p>
        </div>
      </div>
    );

  if (!isOwnBrowser)
    return (
      <div className="dl-minimal-wrapper">
        <div
          className="dl-section dl-loading"
          style={{
            alignItems: "center",
            justifyContent: "center",
            minWidth: "320px",
            textAlign: "center",
          }}
        >
          <Shield size={44} style={{ color: "#94a3b8" }} />
          <p
            style={{ marginTop: "20px", color: "#94a3b8", fontSize: "0.9rem", maxWidth: "320px" }}
          >
            This page is only available from GCA Browser. Download it below to continue.
          </p>
          <button
            className="dl-action-btn"
            onClick={handleDownloadSecureBrowser}
            style={{ marginTop: "16px" }}
          >
            <DownloadIcon size={20} />
            Download GCA Browser
          </button>
        </div>
      </div>
    );

  if (loading)
    return (
      <div className="dl-minimal-wrapper">
        <div
          className="dl-section dl-loading"
          style={{
            alignItems: "center",
            justifyContent: "center",
            minWidth: "320px",
          }}
        >
          <div className="dl-spinner-modern"></div>
          <p
            style={{ marginTop: "20px", color: "#94a3b8", fontSize: "0.9rem" }}
          >
            Securing workstation tunnel...
          </p>
        </div>
      </div>
    );

  return (
    <div className="dl-minimal-wrapper">
      {/* Mobile overlay — hidden on desktop via CSS, shown on mobile until dismissed */}
      {!showAnyway && (
        <div className="dl-mobile-overlay">
          <div className="dl-mobile-overlay-card">
            <div className="dl-mobile-overlay-icon">
              <Monitor size={44} />
            </div>
            <h2>Windows System Required</h2>
            <p>
              The VisuN AI agent installer is designed for <strong>Windows computers</strong>.
              Downloads from a mobile device will not run on your phone.
            </p>
            <p className="dl-mobile-overlay-hint">
              Open this page on your Windows PC to deploy agents.
            </p>
            <button
              className="dl-show-anyway-btn"
              onClick={() => setShowAnyway(true)}
            >
              Show Anyway
            </button>
          </div>
        </div>
      )}

      <div className="dl-minimal-container">
        <div className="dl-minimal-content">
          {/* LEFT: IDENTITY & CONFIG */}
          <section className="dl-section">
            <div className="dl-section-header">
              <div className="header-icon-box">
                <Monitor size={20} />
              </div>
              <h2>1. Configuration</h2>
            </div>

            <div className="dl-field">
              <label className="dl-field-label">Organization Context</label>
              <div className="dl-key-card">
                <code>{enrollmentKey}</code>
                <button
                  onClick={copyToClipboard}
                  className="dl-icon-button"
                  title="Copy Key"
                >
                  {copied ? (
                    <Check size={16} color="#00f2c3" />
                  ) : (
                    <Copy size={16} />
                  )}
                </button>
              </div>
            </div>

            <div className="dl-field">
              <div className="dl-field-label-row">
                <label className="dl-field-label">
                  User Name / Identifier
                </label>
                {isSsoProfile && profile.role !== "employee" && (
                  <label className="dl-autofill-toggle">
                    <input
                      type="checkbox"
                      checked={autoFillFromSso}
                      onChange={(e) => setAutoFillFromSso(e.target.checked)}
                    />
                    <span className="dl-autofill-slider" />
                    <span className="dl-autofill-toggle-text">Use my SSO account name</span>
                  </label>
                )}
              </div>
              <div className="dl-input-wrapper">
                <input
                  type="text"
                  className="dl-input-modern"
                  placeholder="e.g. John Doe"
                  value={agentId}
                  onChange={(e) => setAgentId(e.target.value)}
                  disabled={autoFillFromSso || profile?.role === "employee"}
                />
              </div>
              <p
                className="dl-note"
                style={{
                  color: "#94a3b8",
                  fontSize: "0.75rem",
                  marginTop: "6px",
                }}
              >
                {profile?.role === "employee"
                  ? "Locked to your SSO identity, so this device is linked to your account automatically."
                  : "This name will be used to identify this device in the dashboard."}
              </p>
              {!agentId.trim() && (
                <p className="dl-input-hint">
                  <AlertCircle size={12} /> Name required for deployment
                </p>
              )}
            </div>

            <div className="dl-process-card" style={{ marginTop: 'auto' }}>
              <h4>
                <Info size={14} /> Setup Instructions
              </h4>
              <p>
                1. Enter the <b>User Name</b> above.<br />
                2. Click <b>One-Click Deploy</b> to get all files.<br />
                3. Run <b>VisuN AI-Installer.exe</b> to complete setup.
              </p>
            </div>
          </section>

          {/* RIGHT: ONE-CLICK DEPLOY */}
          <section className="dl-section">
            <div className="dl-section-header">
              <div className="header-icon-box">
                <Shield size={20} />
              </div>
              <h2>2. Deployment</h2>
            </div>

            <div className="dl-deployment-hero">
              <div className="hero-icon-stack">
                <div className="icon-blob user">
                  <User size={22} />
                </div>
                <div className="icon-plus">+</div>
                <div className="icon-blob installer">
                  <DownloadIcon size={22} />
                </div>
              </div>
              <h3>Fast Installation</h3>
              <p>Get everything you need to start monitoring in one package.</p>
              {agentVersion && (
                <span className="dl-version-badge" title="Agent build you're about to download">
                  Agent v{agentVersion}
                </span>
              )}
            </div>

            <button
              className="dl-action-btn one-click-btn"
              onClick={handleFullSuiteDownload}
              disabled={enrollmentKey === "NOT_ASSIGNED" || !agentId.trim()}
            >
              <CheckCircle size={20} />
              One-Click Deploy
            </button>
            <p className="dl-btn-subtext">
              Downloads agent-user{agentVersion ? ` v${agentVersion}` : ""}, env, and VisuN AI-Installer
            </p>

            <div className="dl-divider">
              <span>OR INDIVIDUAL FILES</span>
            </div>

            <div className="dl-mini-downloads">
              <button className="dl-mini-btn" onClick={handleDownloadUserZip}>
                <FileText size={14} /> Agent (.zip){agentVersion ? ` v${agentVersion}` : ""}
              </button>
              <button className="dl-mini-btn" onClick={handleDownloadEnv} disabled={!agentId.trim()}>
                <Copy size={14} /> Config (.env)
              </button>
              <button className="dl-mini-btn" onClick={handleDownloadInstaller}>
                <DownloadIcon size={14} /> Installer (.exe){agentVersion ? ` v${agentVersion}` : ""}
              </button>
            </div>

          </section>
        </div>

        {error && (
          <div className="dl-error-toast">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Windows / Linux / Mac tabs — Linux and Mac are visibly present but
// disabled, per spec, since only the Windows agent actually exists today
// (confirmed: no Linux/Mac build artifacts or download URLs anywhere in the
// codebase — CLAUDE.md notes the Python agent is Windows-only).
const AGENT_TABS = [
  { id: "windows", label: "Windows" },
  { id: "linux", label: "Linux", disabled: true },
  { id: "mac", label: "Mac", disabled: true },
];

function ComingSoonPanel({ os }) {
  return (
    <div className="dl-minimal-wrapper">
      <div
        className="dl-section dl-loading"
        style={{ alignItems: "center", justifyContent: "center", minWidth: "320px", textAlign: "center" }}
      >
        <Monitor size={44} style={{ color: "#94a3b8" }} />
        <p style={{ marginTop: "20px", color: "#94a3b8", fontSize: "0.9rem", maxWidth: "320px" }}>
          The {os} agent isn't available yet — Windows is the only supported platform today.
        </p>
      </div>
    </div>
  );
}

function AgentPage() {
  const [activeTab, setActiveTab] = useState("windows");
  return (
    <div>
      <div style={{ padding: "10px 24px 0" }}>
        <Tabs tabs={AGENT_TABS} activeId={activeTab} onChange={setActiveTab} />
      </div>
      {activeTab === "windows" && <WindowsAgentPanel />}
      {activeTab === "linux" && <ComingSoonPanel os="Linux" />}
      {activeTab === "mac" && <ComingSoonPanel os="Mac" />}
    </div>
  );
}

export default AgentPage;
